/*
 * Copyright 2022 Benjamin Menant.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    Card,
    CardHeader,
    CardBody,
    Text,
    TextContent,
    Title,
    Level,
    LevelItem,
    ExpandableSection,
    CardTitle,
} from '@patternfly/react-core';
import {useTranslation} from "react-i18next";
import type {
    IAMCardMandateJsonSchema,
} from '../../donor-service/types/IAMRecurringCommitmentJsonSchema';
import { FORMAT_DATE_ONLY, formatDate } from '../../utils/formatDate';
import {formatNumber} from "../../utils/formatNumber.ts";
import {useEnvironmentWithDonorService} from "../../context.tsx";

export type CardRecurringCommitmentProps = {
    commitment: IAMCardMandateJsonSchema;
};

export function CardRecurringCommitment({
    commitment: {
        amount,
        cardExpiry,
        cardNumber,
        mandateId,
        nextDebitDate,
        periodicity,
    },
}: CardRecurringCommitmentProps) {
    const { t } = useTranslation();
    const context = useEnvironmentWithDonorService();

    const commitmentNumber = String(mandateId)
        .padStart(6, '0')
        .match(/\w{1,3}/g)!.join('\u202f');

    const redactedCardNumber = cardNumber
        ? cardNumber.slice(-4).padStart(16, 'X').match(/\w{1,4}/g)!.join('\u202f')
        : t("cardUnknown");

    const _nextDebitDate = new Date(nextDebitDate);
    const prettyNextDebitDate = isNaN(_nextDebitDate.valueOf())
        ? t("nextDebitDateUnknown")
        : formatDate(_nextDebitDate, context.environment.locale, FORMAT_DATE_ONLY);

    const _cardExpiry = new Date(cardExpiry || '');
    const prettyCardExpiry = isNaN(_cardExpiry.valueOf())
        ? undefined
        : formatDate(_cardExpiry, context.environment.locale, FORMAT_DATE_ONLY);

    const prettyAmount = formatNumber(amount);

    return (
        <Card className="ape-c-card">
            <CardHeader
                actions={{
                    actions: [<Title key={commitmentNumber} size="xl" headingLevel="h4">№ {commitmentNumber}</Title>],
                    hasNoOffset: true,
                }}
            >
                <CardTitle>
                    <Level hasGutter>
                        <LevelItem><Title size="4xl" headingLevel="h2">💳</Title></LevelItem>
                        <LevelItem><Title size="xl" headingLevel="h2">Carte Bancaire</Title></LevelItem>
                        <LevelItem>
                            <Title size="xl" headingLevel="h3">
                                <strong>{prettyAmount} {t(periodicity)}</strong>
                            </Title>
                        </LevelItem>
                    </Level>
                </CardTitle>
            </CardHeader>
            <CardBody>
                <Text>{t("cardNumber")} {redactedCardNumber}</Text>
                { prettyCardExpiry && <Text>{t("cardExpiry")} {prettyCardExpiry}</Text> }
                <Text>{t("nextDebitDate")} {prettyNextDebitDate}</Text>
            </CardBody>
            <CardBody>
                <ExpandableSection isIndented toggleText={t("EditOrSuspend")}>
                    <TextContent>
                        <Text>
                            <Text component="a" href={t('contactPageHref')} target="_blank" rel="noreferrer noopener">
                                {t("doContactUs")}
                            </Text> {t("contactUsToEditOrSuspend", { ref: commitmentNumber })}
                        </Text>
                    </TextContent>
                </ExpandableSection>
            </CardBody>
        </Card>
    )
}
